import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import "./OfferingDetail.css";
import "../../Vocabularies/Crud/ResourceDetail.css";
import "../../DataOfferings/DataOfferings.css";
import "../../Vocabularies/DataModel.css";
import DeleteButton from "../../../components/buttons/DeleteButton";
import EditButton from "../../../components/buttons/EditButton";
import BreadCrumb from "../../../components/HeaderComponents/BreadCrum";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { Spinner } from "../../../components/Filtering/Reusable/Spinner";
import { useTableControls } from "../../../hooks/useTableControls";
import { useWorkerApiHelper } from "../../../hooks/useWorkerApiHelper";
import { useGetDatasetById } from "../../../hooks/datasets/useGetDatasetById";
import { useGetDistributionsByIds } from "../../../hooks/distributions/useGetDistributionsByIds";
import { useGetParticipantsOld } from "../../../hooks/participants/useGetParticipantsOld";
import { useGetAgents } from "../../../hooks/agents/useGetAgents";
import {
  getTextByLang,
  formatArray,
  renderLinks,
} from "../../../utils/formUtils";
import { formatDate } from "../../../utils/constants";
import ExpandableText from "../../../components/ExpandableText/ExpandableText";
import { showErrorAlert } from "../../../utils/showErrorAlert";
import { extractIdFromIri } from "../../../utils/extractFromIri";
import {
  buildParticipantsById,
  getParticipantLabelById,
} from "../../../utils/participantLookup";

export default function OfferingDetail() {
  const { callApiWithWorker } = useWorkerApiHelper();
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const record = location.state?.record;
  const identifier =
    routeId ?? record?.identifier ?? record?.resource_id ?? record?.dataset_id;
  const currentLang = (i18n.language || "es").split("-")[0].toLowerCase();
  const { orgProvider, setTableData } = useTableControls([]);
  const [datasetService, setDatasetService] = useState(null);

  const { data: participantsResponse } = useGetParticipantsOld({
    callApiWithWorker,
    lang: currentLang,
    pageIndex: 1,
    pageSize: 1000,
  });
  const { data: agentsResponse } = useGetAgents({
    callApiWithWorker,
    enabled: true,
  });

  const getContracts = async () => {
    try {
      const resp = await callApiWithWorker("api/observability/contracts", {
        method: "GET",
        headers: { authority: "" },
      });

      const data = resp?.result ?? [];
      return data;
    } catch (error) {
      console.error("Error fetching contracts:", error);
      return [];
    }
  };

  const parsePayload = (response) => {
    if (typeof response?.body === "string") {
      try {
        return JSON.parse(response.body);
      } catch {
        return response?.data ?? response;
      }
    }
    return response?.data ?? response;
  };

  const { data: datasetByIdResponse } = useGetDatasetById({
    callApiWithWorker,
    id: identifier,
    language: currentLang,
    enabled: Boolean(identifier),
  });

  useEffect(() => {
    if (datasetService) {
      setTableData([datasetService]);
    }
  }, [datasetService, setTableData]);

  useEffect(() => {
    if (!datasetByIdResponse) return;
    const payload = parsePayload(datasetByIdResponse);
    const raw =
      payload?.item ||
      payload?.data ||
      payload?.dataset ||
      payload?.content?.[0] ||
      payload;
    if (!raw) return;
    setDatasetService(normalizeOfferingForDetail(raw));
  }, [datasetByIdResponse, i18n.language]);

  const deleteOffering = async (identifier) => {
    try {
      const contracts = await getContracts();

      const isReferencedInContracts = contracts?.some(
        (contract) =>
          String(contract.dataset_id).trim() === String(identifier).trim(),
      );

      let userConfirmed = false;

      if (isReferencedInContracts) {
        const extraCheck = await Swal.fire({
          title: t("dataOfferings.deleteMessage"),
          html: `
            <p>${t("dataOfferings.deleteExtraMessage")}</p>
            <div style="margin-top: 1em;">
              <input type="checkbox" id="confirmCheckbox" />
              <label for="confirmCheckbox">${t(
                "dataOfferings.deleteConfirmation",
              )}</label>
            </div>
          `,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: t("dataOfferings.deleteMessageYes"),
          cancelButtonText: t("dataOfferings.deleteMessageNo"),
          didOpen: () => {
            const checkbox = document.getElementById("confirmCheckbox");
            const confirmBtn = Swal.getConfirmButton();
            confirmBtn.disabled = true;
            checkbox.addEventListener("change", () => {
              confirmBtn.disabled = !checkbox.checked;
            });
          },
        });

        if (!extraCheck.isConfirmed) return;
        userConfirmed = true;
      }

      if (!userConfirmed) {
        const result = await Swal.fire({
          title: t("dataOfferings.deleteMessage"),
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: t("dataOfferings.deleteMessageYes"),
          cancelButtonText: t("dataOfferings.deleteMessageNo"),
        });

        if (!result.isConfirmed) return;
      }

      setLoading(true);

      try {
        const response = await callApiWithWorker(
          `/api/dsb/catalog/resource/delete?fileHash=${identifier}`,
          { method: "DELETE" },
        );

        setLoading(false);

        const success = response?.data?.data || response?.data || response;

        if (success) {
          await Swal.fire({
            title: t("dataOfferings.deleteSuccesfulTitle"),
            text: t("dataOfferings.deleteSuccesful"),
            icon: "success",
          });

          navigate("/dataoffering");
        }
      } catch (error) {
        setLoading(false);
        console.error("Error deleting resource:", error);
        await showErrorAlert(error);
      }
    } catch (swalError) {
      console.error("Error showing confirmation modal:", swalError);
    }
  };

  const toArray = (value) =>
    Array.isArray(value) ? value.filter(Boolean) : value ? [value] : [];
  const getOptionalTextByLang = (field) => {
    if (
      field === null ||
      typeof field === "undefined" ||
      (Array.isArray(field) && field.length === 0)
    ) {
      return "";
    }

    const text = getTextByLang(field, currentLang);
    return text === "Not specified." ? "" : text;
  };

  const distributionIds = useMemo(
    () =>
      toArray(datasetService?.distributions).map((d) => extractIdFromIri(d) || d),
    [datasetService?.distributions],
  );
  const { data: distributionsByIds } = useGetDistributionsByIds({
    callApiWithWorker,
    ids: distributionIds,
    language: currentLang,
    enabled: distributionIds.length > 0,
  });

  const participantsById = useMemo(
    () => buildParticipantsById(participantsResponse?.content),
    [participantsResponse?.content],
  );

  const normalizeOfferingForDetail = (raw = {}) => {
    const metadata = raw?.metadata ?? {};

    return {
      ...raw,
      title:
        getOptionalTextByLang(raw?.title) ||
        getOptionalTextByLang(raw?.name) ||
        getOptionalTextByLang(metadata?.title) ||
        "",
      description:
        getOptionalTextByLang(raw?.description) ||
        getOptionalTextByLang(metadata?.description) ||
        "",
      versionNotes:
        getOptionalTextByLang(raw?.versionNotes) ||
        getOptionalTextByLang(raw?.version_notes) ||
        getOptionalTextByLang(metadata?.version_notes) ||
        "",
      version:
        raw?.version ?? raw?.version_info ?? metadata?.version_info ?? "",
      extension:
        raw?.extension ??
        raw?.resource_extension ??
        metadata?.extension ??
        metadata?.resource_extension ??
        "",
      modified: raw?.modified ?? raw?.updated_at ?? raw?.created_at ?? "",
      qualityScore: raw?.qualityScore ?? raw?.quality_score ?? null,
      creator: toArray(raw?.creator ?? metadata?.creator),
      publisher: raw?.publisher ?? metadata?.publisher ?? "",
      theme: toArray(raw?.theme ?? metadata?.theme),
      frequency: raw?.frequency ?? metadata?.frequency ?? "",
      language: toArray(raw?.language ?? metadata?.language),
      geographicalCoverage: toArray(
        raw?.geographicalCoverage ?? raw?.spatial ?? metadata?.spatial,
      ),
      documentation: toArray(
        raw?.documentation ?? raw?.landing_page ?? metadata?.documentation,
      ),
      status: raw?.status ?? metadata?.status ?? "",
      vocabulary: raw?.vocabulary ?? metadata?.vocabulary ?? "",
      keywords: toArray(raw?.keywords ?? metadata?.keywords),
      distributions: toArray(raw?.distributions ?? metadata?.distributions),
      accessRights: raw?.access_rights ?? metadata?.access_rights ?? "",
      applicableLegislation: toArray(
        raw?.applicable_legislation ?? metadata?.applicable_legislation,
      ),
      landingPage: raw?.landing_page ?? metadata?.landing_page ?? "",
      id: raw?.id ?? metadata?.id ?? "",
      identifier: raw?.identifier ?? metadata?.identifier ?? "",
    };
  };

  const formatIriValue = (value) => {
    if (!value) return "—";
    if (typeof value !== "string") return value;
    return extractIdFromIri(value) || value;
  };

  const agentNamesById = useMemo(() => {
    return (agentsResponse?.agents ?? []).reduce((acc, agent) => {
      const rawId = String(agent?.id ?? "").trim();
      const compactId = extractIdFromIri(rawId);
      const label = String(agent?.name ?? "").trim();

      if (!label) return acc;
      if (rawId) acc[rawId] = label;
      if (compactId) acc[compactId] = label;

      return acc;
    }, {});
  }, [agentsResponse?.agents]);

  const publisherLabel = useMemo(() => {
    return getParticipantLabelById(
      datasetService?.publisher,
      participantsById,
      currentLang,
    );
  }, [currentLang, datasetService?.publisher, participantsById]);

  const creatorLabels = useMemo(() => {
    return (Array.isArray(datasetService?.creator) ? datasetService.creator : [])
      .map((creatorId) => {
        const participantLabel = getParticipantLabelById(
          creatorId,
          participantsById,
          currentLang,
        );

        if (participantLabel && participantLabel !== formatIriValue(creatorId)) {
          return participantLabel;
        }

        const rawId = String(creatorId ?? "").trim();
        const compactId = extractIdFromIri(rawId);

        return (
          agentNamesById[rawId] ||
          agentNamesById[compactId] ||
          participantLabel ||
          formatIriValue(creatorId)
        );
      })
      .filter(Boolean);
  }, [agentNamesById, currentLang, datasetService?.creator, participantsById]);

  const distributionNames = useMemo(() => {
    if (!distributionIds.length) return {};

    const normalized = Array.isArray(distributionsByIds)
      ? distributionsByIds
      : [];

    const entries = normalized.map((item, index) => {
      const id =
        extractIdFromIri(item?.id) || extractIdFromIri(item?.identifier) || "";
      const fallbackId = distributionIds[index];
      const safeId = id || fallbackId;

      const title =
        getOptionalTextByLang(item?.title) ||
        getOptionalTextByLang(item?.name) ||
        item?.identifier ||
        formatIriValue(safeId);

      return [safeId, title];
    });

    return Object.fromEntries(entries);
  }, [distributionIds, distributionsByIds, i18n.language]);

  if (!datasetService) {
    return (
      <div className="spinner-container">
        <Spinner size="large" color="var(--main-color)" />
      </div>
    );
  }

  return (
    <section className="pb6 offering-detail-page">
      <div className="w100 flexbread">
        <div
          className="divbreadcrum w90"
          style={{ display: "flex", alignItems: "center" }}
        >
          <BreadCrumb />
        </div>
      </div>
      <div className="container containerflex offering-detail-shell">
        <div className="resourcetitlediv w90 offering-detail-header">
          <div className="left-block">
            <button
              onClick={() => navigate("/dataoffering")}
              className="caret-left-button-1"
            >
              <ArrowBackIosIcon />
            </button>
            <h2
              className="tittlecatalog offering-detail-title"
              title={datasetService?.title}
            >
              {datasetService?.title}
            </h2>
            {datasetService?.contracted && (
              <div className="conctracteddata">{t("contracted")}</div>
            )}
          </div>
          {orgProvider && (
            <div className="flexdivbotonesdetailpage">
              <div className="plr6">
                <DeleteButton onClick={() => deleteOffering(identifier)} />
              </div>
              <div>
                <EditButton
                  onClick={() =>
                    navigate(`/editdataoffering/${identifier}`, {
                      state: { record: datasetService, datasetService },
                    })
                  }
                />
              </div>
            </div>
          )}
        </div>
        <div className="divgris w90 offering-detail-kpis">
          <div className="divgriselement">
            <h3 className="ptitulodetalle">
              {t("dataOfferingsDetail.detail.theme")}
            </h3>
            <p className="theme-container">
              {Array.isArray(datasetService?.theme) ? (
                datasetService.theme.map((themeItem, index) => (
                  <span key={index} className="themedata">
                    {formatIriValue(themeItem)}
                  </span>
                ))
              ) : (
                <span className="themedata">
                  {datasetService?.theme || "—"}
                </span>
              )}
            </p>
          </div>

          <div className="divgriselement">
            <h3 className="ptitulodetalle">
              {t("dataOfferingsDetail.detail.creator")}
            </h3>
            <p>{formatArray(creatorLabels)}</p>
          </div>
          <div className="divgriselement">
            <h3 className="ptitulodetalle">
              {t("dataOfferingsDetail.detail.publisher")}
            </h3>
            <p>{publisherLabel}</p>
          </div>
          <div className="divgriselement">
            <h3 className="ptitulodetalle">
              {t("dataOfferingsDetail.detail.version")}
            </h3>
            <p>
              <span className="licensedata">{datasetService?.version}</span>
            </p>
          </div>
          <div className="divgriselement">
            <h3 className="ptitulodetalle">
              {t("dataOfferingsDetail.detail.lastUpdate")}
            </h3>
            <p>{formatDate(datasetService?.modified)}</p>
          </div>
        </div>
      </div>
      <div className="containerflexstart container offering-detail-main">
        <div className="w90 bgwhite offering-detail-content-card">
          <div className="divdescriptionOffer plr2">
            <div className="left-column">
              <div className="bordergris">
                <h4 className="mtb descriptiontitle">
                  {t("dataOfferingsDetail.detail.description")}
                </h4>
                <ExpandableText
                  text={datasetService?.description}
                  maxLines={3}
                />
              </div>
              <div className="bordergris">
                <h4 className="mtb descriptiontitle">
                  {t("dataOfferingsDetail.detail.version_notes")}
                </h4>
                <ExpandableText
                  text={datasetService?.versionNotes}
                  maxLines={3}
                />
              </div>
            </div>
          </div>
          <div className="divvocabulary offering-detail-fields-grid">
            <div className="p2">
              <div className="mtb">
                <h4 className="descriptiontitle">
                  {t("dataOfferingsDetail.detail.frequency")}
                </h4>
              </div>
              <div className="mtb2">
                <p className="pdetails">
                  {formatIriValue(datasetService?.frequency)}
                </p>
              </div>
            </div>
            <div className="p2">
              <div className="mtb">
                <h4 className="descriptiontitle">
                  {t("dataOfferingsDetail.detail.language")}
                </h4>
              </div>
              <div className="mtb2">
                <p className="pdetails">
                  {formatArray(datasetService?.language?.map(formatIriValue))}
                </p>
              </div>
            </div>
            <div className="p2">
              <div className="mtb">
                <h4 className="descriptiontitle">
                  {t("wizard.steps.dataset.spatial")}
                </h4>
              </div>
              <div className="mtb2">
                <p className="pdetails">
                  {formatArray(
                    datasetService?.geographicalCoverage?.map(formatIriValue),
                  )}
                </p>
              </div>
            </div>

            <div className="p2">
              <div className="mtb">
                <h4 className="descriptiontitle">
                  {t("dataOfferingsDetail.detail.documentation")}
                </h4>
              </div>
              {renderLinks(datasetService?.documentation)}
            </div>
            <div className="p2">
              <div className="mtb">
                <h4 className="descriptiontitle">
                  {t("wizard.steps.dataset.keywords")}
                </h4>
              </div>
              <div className="mtb2">
                <p className="pdetails">
                  {formatArray(datasetService?.keywords)}
                </p>
              </div>
            </div>
            <div className="p2">
              <div className="mtb">
                <h4 className="descriptiontitle">
                  {t("wizard.steps.dataset.distribution.title")}
                </h4>
              </div>
              <div className="mtb2">
                <p className="pdetails">
                  {toArray(datasetService?.distributions)
                    .map((dist) => {
                      const id = extractIdFromIri(dist) || dist;
                      return distributionNames[id] || formatIriValue(id);
                    })
                    .join(", ") || "—"}
                </p>
              </div>
            </div>
            <div className="p2">
              <div className="mtb">
                <h4 className="descriptiontitle">
                  {t("wizard.steps.dataset.rights")}
                </h4>
              </div>
              <div className="mtb2">
                <p className="pdetails">
                  {formatIriValue(datasetService?.accessRights)}
                </p>
              </div>
            </div>
            <div className="p2">
              <div className="mtb">
                <h4 className="descriptiontitle">
                  {t("wizard.steps.dataset.legislation")}
                </h4>
              </div>
              <div className="mtb2">
                <p className="pdetails">
                  {renderLinks(datasetService?.applicableLegislation)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        {loading && (
          <div className="loading">
            <Spinner size="large" color="var(--main-color)" />
          </div>
        )}
      </div>
    </section>
  );
}
